import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Ear, HandHeart, ArrowRight, PlayCircle, ShieldCheck,
  Sunrise, Heart, EyeOff,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { createSample, saveOwnership } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/6918479/pexels-photo-6918479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const nav = useNavigate();
  const [loadingSample, setLoadingSample] = useState(false);

  const handleSample = async () => {
    setLoadingSample(true);
    try {
      const r = await createSample();
      saveOwnership(r.id, r.owner_token, r.share_token);
      toast.success("Sample letter ready.");
      nav(`/letter/${r.id}`);
    } catch (e) {
      toast.error("Could not load the sample. Please try again.");
    } finally {
      setLoadingSample(false);
    }
  };

  return (
    <>
      <Header />
      <main data-testid="landing-page" className="mx-auto max-w-6xl px-6 pt-12 pb-6">
        <section className="grid gap-14 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          {/* Left: copy */}
          <div className="cn-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white px-4 py-2 text-sm font-semibold text-moss-700">
              <ShieldCheck strokeWidth={2.5} className="h-4 w-4" />
              Private · No login required
            </span>
            <h1
              className="mt-6 font-display text-6xl font-bold leading-[1.05] text-navy sm:text-7xl lg:text-8xl"
              data-testid="hero-tagline"
            >
              Snap it. <span className="text-moss-700">Hear it.</span> Handle it.
            </h1>
            <p className="mt-6 max-w-xl text-2xl leading-relaxed text-slate-700 sm:text-2xl">
              Older adults get confusing letters from Medicare, insurance, utilities,
              benefits offices, and landlords. Deadlines get missed. Important details
              get lost. LetterLifts reads the letter, explains it kindly in plain language,
              and quietly keeps a trusted family member in the loop.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/upload"
                data-testid="cta-upload-letter"
                className="inline-flex items-center gap-3 rounded-full bg-moss-700 px-8 py-4 text-lg font-bold text-bone shadow-md hover:bg-moss-800 transition-colors min-h-[56px]"
              >
                <Camera strokeWidth={2.5} className="h-6 w-6" />
                Upload a Letter
                <ArrowRight strokeWidth={2.5} className="h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={handleSample}
                disabled={loadingSample}
                data-testid="cta-try-sample"
                className="inline-flex items-center gap-3 rounded-full border-2 border-moss-700 bg-white px-7 py-4 text-lg font-bold text-moss-700 hover:bg-moss-50 transition-colors min-h-[56px] disabled:opacity-60"
              >
                <PlayCircle strokeWidth={2.5} className="h-6 w-6" />
                {loadingSample ? "Loading sample…" : "Try with a sample letter"}
              </button>
            </div>
          </div>

          {/* Right: image with warm treatment + quote */}
          <div className="cn-fade-up">
            <div className="relative overflow-hidden rounded-3xl border-2 border-moss-700/25 bg-white shadow-md">
              <img
                src={HERO_IMG}
                alt="Two women reading a letter together at a kitchen table."
                className="h-[380px] w-full object-cover sm:h-[460px]"
                style={{ filter: "saturate(1.18) contrast(1.02) sepia(0.14)" }}
              />
              {/* Warm golden overlay */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255, 197, 122, 0.25) 0%, rgba(168, 58, 31, 0.08) 55%, rgba(0,0,0,0) 100%)",
                }}
              />
              <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/95 p-5 border-2 border-slate-200">
                <p className="text-base font-semibold text-slate-900">
                  &ldquo;It read Mom&rsquo;s Medicare letter to her and emailed me a summary. She actually understood it.&rdquo;
                </p>
                <p className="mt-1 text-sm text-slate-500">— The daughter we built this for.</p>
              </div>
            </div>
          </div>
        </section>

        {/* NEW: Built for the people we love */}
        <section
          className="relative mt-24 overflow-hidden rounded-3xl border-2 border-ember-600/30 bg-gradient-to-br from-ember-50 via-white to-white p-8 sm:p-12"
          data-testid="section-people-we-love"
          aria-labelledby="people-we-love-heading"
        >
          <span
            aria-hidden="true"
            className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-ember-100/60 blur-3xl"
          />
          <span
            aria-hidden="true"
            className="absolute -right-24 -bottom-24 h-72 w-72 rounded-full bg-moss-100/60 blur-3xl"
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-ember-700 bg-white px-4 py-2 text-sm font-bold uppercase tracking-[0.14em] text-ember-700">
              <Sunrise strokeWidth={2.5} className="h-4 w-4" />
              A warmer way to handle paperwork
            </div>
            <h2
              id="people-we-love-heading"
              className="mt-5 font-display text-5xl font-bold leading-tight text-slate-900 sm:text-6xl"
            >
              Built for the people we love.
            </h2>
            <div className="mt-6 grid gap-5 text-xl leading-relaxed text-slate-800 sm:text-2xl sm:leading-[1.5] max-w-3xl">
              <p>
                Letters from Medicare, insurance, and landlords shouldn&rsquo;t be scary.
                LetterLifts explains them with patience and respect &mdash; and makes sure
                someone who loves you has your back.
              </p>
              <p className="text-lg sm:text-xl text-slate-700">
                No accounts. No surveillance. <span className="font-semibold text-moss-700">You decide what&rsquo;s shared.</span>
              </p>
            </div>

            {/* Warm badge row */}
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                { icon: Heart, label: "Patient by design" },
                { icon: EyeOff, label: "No surveillance" },
                { icon: HandHeart, label: "Family, only if you say so" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-ember-600/40 bg-white px-4 py-2 text-base font-semibold text-slate-800"
                >
                  <Icon strokeWidth={2.5} className="h-4 w-4 text-ember-700" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Three-step strip */}
        <section className="mt-24 grid gap-6 sm:grid-cols-3" aria-label="How LetterLifts works">
          {[
            {
              icon: Camera,
              title: "1. Snap it",
              body: "Take a photo of the letter or upload a file. That's the whole setup.",
            },
            {
              icon: Ear,
              title: "2. Hear it",
              body: "We explain it in calm, plain words — and read it aloud if you'd like.",
            },
            {
              icon: HandHeart,
              title: "3. Handle it",
              body: "See the deadline. Save it to your calendar. Loop in someone you trust.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="cn-card-lift rounded-3xl border-2 border-slate-200 bg-white p-8"
              data-testid={`step-card-${title.split(".")[0]}`}
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-moss-700 text-bone">
                <Icon strokeWidth={2.5} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">{title}</h3>
              <p className="mt-3 text-lg leading-relaxed text-slate-700">{body}</p>
            </article>
          ))}
        </section>

        {/* Trust strip */}
        <section className="mt-20 rounded-3xl border-2 border-slate-200 bg-white p-8 sm:p-10">
          <h2 className="font-display text-4xl font-bold text-slate-900 sm:text-5xl">
            Made for calm, not for hurry.
          </h2>
          <ul className="mt-6 grid gap-4 text-lg leading-relaxed text-slate-700 sm:grid-cols-2">
            <li>· No accounts. No passwords. No sign-up.</li>
            <li>· The original letter is never posted to the internet.</li>
            <li>· Family sees only what the senior chooses to share.</li>
            <li>· General document assistance — not legal, medical, or financial advice.</li>
          </ul>
        </section>

        {/* Closing line above footer */}
        <section
          className="mt-20 mb-4 rounded-3xl border-2 border-moss-700/25 bg-gradient-to-r from-ember-50 via-white to-ember-50 py-14 px-6 text-center"
          data-testid="closing-line"
        >
          <p className="font-display text-3xl font-bold text-slate-900 sm:text-4xl leading-tight">
            Support without surveillance.<br className="hidden sm:block" />
            <span className="text-moss-700"> Help without hovering.</span>
          </p>
        </section>
      </main>
      <Footer />
    </>
  );
}
